import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { X } from 'lucide-react';
import dayjs from 'dayjs';
import api from '../../services/api';
import { equipmentMaintenanceApi } from '../../services/equipmentMaintenanceService';

interface CreateEquipmentMaintenanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  stores: { number: string; name: string }[];
  rgms: string[];
  areaCoaches: string[];
  regionalCoaches: string[];
  categories: { id: number; name: string }[];
  selectedStore: { number: string; name: string } | null;
}

interface SubCategory {
  id: number;
  name: string;
  requisition_category_id: number;
}

export default function CreateEquipmentMaintenanceModal({
  isOpen,
  onClose,
  onSubmit,
  stores,
  rgms,
  areaCoaches,
  regionalCoaches,
  categories,
  selectedStore
}: CreateEquipmentMaintenanceModalProps) {
  const [formData, setFormData] = useState({
    date: dayjs().format('YYYY-MM-DD'),
    store: '',
    rgm: '',
    area_coach: '',
    regional_coach: '',
    description: '',
    category_id: '' as string | number,
    subcategory_id: '' as string | number,
    serial_number: '',
    quantity: 1
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [filteredDescriptions, setFilteredDescriptions] = useState<string[]>([]);
  const [loadingDescriptions, setLoadingDescriptions] = useState(false);
  
  // const uniqueRgms = useMemo(() => {
  //   // Use hardcoded values if properties don't exist
  //   return ["MORGAN"]; // Based on your screenshot
  // }, []);

  // const uniqueAreaCoaches = useMemo(() => {
  //   // Use hardcoded values if properties don't exist
  //   return ["Strydom Naidoo"]; // Based on your screenshot
  // }, []);

  // const uniqueRegionalCoaches = useMemo(() => {
  //   // Use hardcoded values if properties don't exist
  //   return ["Strydom Naidoo"]; // Based on your screenshot
  // }, []);

  // const categories = useMemo(() => {
  //   // Get unique category values
  //   return ["NULL", "19", "22", "23"];
  // }, []);

  useEffect(() => {
    if (isOpen) {
      const fetchSubcategories = async () => {
        setLoadingSubcategories(true);
        try {
          const response = await api.get(`/equipment-maintenance/subcategories/10`);
          if (response.data && response.data.success) {
            setSubcategories(response.data.data);
          } else {
            console.error("Failed to fetch subcategories. API response issue. Data:", response.data, "Full response status:", response.status);
            setSubcategories([]);
          }
        } catch (err: any) {
          console.error('Error fetching subcategories. Request failed. Error object:', err);
          if (err.response) {
            console.error('Error response data:', err.response.data);
            console.error('Error response status:', err.response.status);
            // console.error('Error response headers:', err.response.headers);
          } else if (err.request) {
            console.error('Error request data:', err.request);
          } else {
            console.error('Error message:', err.message);
          }
          setSubcategories([]);
        } finally {
          setLoadingSubcategories(false);
        }
      };
      fetchSubcategories();
    }
  }, [isOpen, selectedStore]);

  // Fetch coaches when selectedStore changes or modal opens
  useEffect(() => {
    if (selectedStore && isOpen) {
      const fetchCoaches = async () => {
        try {
          const coachesData = await equipmentMaintenanceApi.getCoachesByStore(selectedStore.number);
          setFormData(prev => ({
            ...prev,
            area_coach: coachesData.area_coach || '',
            regional_coach: coachesData.regional_coach || ''
          }));
        } catch (error) {
          console.error('Failed to fetch coaches for store:', error);
        }
      };
      fetchCoaches();
    }
  }, [selectedStore, isOpen]);

  const handleCategoryChange = async (value: string | number) => {
    const categoryId = value ? Number(value) : '';
    
    setFormData(prev => ({
      ...prev,
      category_id: categoryId,
      subcategory_id: '',
      description: ''
    }));
    
    // Clear errors
    if (errors.category_id) {
      setErrors(prev => ({ ...prev, category_id: '' }));
    }
    if (errors.subcategory_id) {
      setErrors(prev => ({ ...prev, subcategory_id: '' }));
    }
    if (errors.description) {
      setErrors(prev => ({ ...prev, description: '' }));
    }

    // Reset subcategories and descriptions
    setSubcategories([]);
    setFilteredDescriptions([]);

    // Fetch subcategories and descriptions for selected category
    if (categoryId) {
      setLoadingSubcategories(true);
      setLoadingDescriptions(true);
      try {
        const [subcats, descs] = await Promise.all([
          equipmentMaintenanceApi.getSubcategories(categoryId),
          equipmentMaintenanceApi.getDescriptionsByCategory(categoryId)
        ]);
        setSubcategories(subcats);
        setFilteredDescriptions(descs);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoadingSubcategories(false);
        setLoadingDescriptions(false);
      }
    }
  };

  const handleSubcategoryChange = async (value: string | number) => {
    const subcategoryId = value ? Number(value) : '';
    
    setFormData(prev => ({
      ...prev,
      subcategory_id: subcategoryId,
      description: '' // Clear description when subcategory changes
    }));
    
    // Clear error for subcategory field
    if (errors.subcategory_id) {
      setErrors(prev => ({ ...prev, subcategory_id: '' }));
    }

    // If subcategory is selected, filter descriptions by subcategory
    if (subcategoryId) {
      setLoadingDescriptions(true);
      try {
        const descriptions = await equipmentMaintenanceApi.getDescriptionsBySubcategory(subcategoryId);
        setFilteredDescriptions(descriptions);
      } catch (error) {
        console.error('Failed to fetch descriptions by subcategory:', error);
        setFilteredDescriptions([]);
      } finally {
        setLoadingDescriptions(false);
      }
    } else {
      // If subcategory is cleared, use all descriptions from category
      if (formData.category_id) {
        // Re-fetch all descriptions for the current category
        setLoadingDescriptions(true);
        try {
          const descriptions = await equipmentMaintenanceApi.getDescriptionsByCategory(Number(formData.category_id));
          setFilteredDescriptions(descriptions);
        } catch (error) {
          console.error('Failed to fetch descriptions by category:', error);
          setFilteredDescriptions([]);
        } finally {
          setLoadingDescriptions(false);
        }
      } else {
        setFilteredDescriptions([]);
      }
    }
  };

  const handleDescriptionChange = async (value: string) => {
    setFormData(prev => ({
      ...prev,
      description: value
    }));
    
    // Clear error for description field
    if (errors.description) {
      setErrors(prev => ({ ...prev, description: '' }));
    }

    // Auto-set subcategory when description is selected
    if (value) {
      try {
        const details = await equipmentMaintenanceApi.getDescriptionDetails(value);
        if (details && details.subcategory_id) {
          setFormData(prev => ({
            ...prev,
            description: value,
            subcategory_id: details.subcategory_id
          }));
        }
      } catch (error) {
        console.error('Failed to fetch description details:', error);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.store) newErrors.store = 'Store is required';
    if (!formData.rgm) newErrors.rgm = 'RGM is required';
    if (!formData.area_coach) newErrors.area_coach = 'Area Coach is required';
    if (!formData.regional_coach) newErrors.regional_coach = 'Regional Coach is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.quantity || formData.quantity < 1) newErrors.quantity = 'Quantity must be at least 1';
    if (!formData.subcategory_id) newErrors.subcategory_id = 'Sub Category is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    
    setIsSubmitting(true);
    try {
      const dataToSubmit = {
        ...formData,
        category_id: 10,
        subcategory_id: formData.subcategory_id ? Number(formData.subcategory_id) : null,
      };
      await onSubmit(dataToSubmit);
      setFormData({
        date: dayjs().format('YYYY-MM-DD'),
        store: selectedStore?.number || '',
        rgm: '',
        area_coach: '',
        regional_coach: '',
        description: '',
        category_id: '' as string | number,
        subcategory_id: '' as string | number,
        serial_number: '',
        quantity: 1
      });
      setErrors({});
      setSubcategories([]);
      setFilteredDescriptions([]);
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setFormData({
        date: dayjs().format('YYYY-MM-DD'),
        store: selectedStore?.number || '',
        rgm: '',
        area_coach: '', // Will be set by the coaches fetching effect
        regional_coach: '', // Will be set by the coaches fetching effect
        description: '',
        category_id: '' as string | number,
        subcategory_id: '' as string | number,
        serial_number: '',
        quantity: 1
      });
      setErrors({});
      setSubcategories([]);
      setFilteredDescriptions([]);
    }
  }, [isOpen]); // Remove selectedStore from dependency to prevent conflicting with coaches effect

  return (
    <Dialog
      open={isOpen}
      onClose={() => !isSubmitting && onClose()}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen">
        <div className="fixed inset-0 bg-black opacity-30" />
        
        <div className="relative bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full mx-4 shadow-xl">
          <div className="border-b border-gray-200 dark:border-gray-700 p-4 flex justify-between items-center">
            <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
              Add Equipment Maintenance Record
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              disabled={isSubmitting}
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category*
                </label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className={`w-full rounded-md border ${errors.category_id ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting}
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {errors.category_id && <p className="mt-1 text-sm text-red-500">{errors.category_id}</p>}
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sub Category
                </label>
                <select
                  name="subcategory_id"
                  value={formData.subcategory_id}
                  onChange={(e) => handleSubcategoryChange(e.target.value)}
                  className={`w-full rounded-md border ${errors.subcategory_id ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting || loadingSubcategories || !formData.category_id}
                >
                  <option value="">{loadingSubcategories ? 'Loading...' : 'Auto-set from description (optional)'}</option>
                  {subcategories.map(subcat => (
                    <option key={subcat.id} value={subcat.id}>
                      {subcat.name}
                    </option>
                  ))}
                </select>
                {errors.subcategory_id && <p className="mt-1 text-sm text-red-500">{errors.subcategory_id}</p>}
                {formData.description && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Subcategory auto-set from description
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description*
                </label>
                <select
                  name="description"
                  value={formData.description}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                  className={`w-full rounded-md border ${errors.description ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting || loadingDescriptions || !formData.category_id}
                >
                  <option value="">{loadingDescriptions ? 'Loading...' : 'Select a description'}</option>
                  {filteredDescriptions.map((desc) => (
                    <option key={desc} value={desc}>{desc}</option>
                  ))}
                </select>
                {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date*
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting}
                />
                {errors.date && <p className="mt-1 text-sm text-red-500">{errors.date}</p>}
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Store*
                </label>
                <select
                  name="store"
                  value={formData.store}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.store ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting || !!selectedStore}
                >
                  <option value="">Select a store</option>
                  {stores.map(store => (
                    <option key={store.number} value={store.number}>
                      {store.name}
                    </option>
                  ))}
                </select>
                {errors.store && <p className="mt-1 text-sm text-red-500">{errors.store}</p>}
                {selectedStore && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Store locked from filter selection
                  </p>
                )}
              </div>

              {/* RGM Input with Datalist */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  RGM*
                </label>
                <input
                  type="text"
                  name="rgm"
                  value={formData.rgm}
                  onChange={handleChange}
                  list="rgm_list"
                  placeholder="Type or select an RGM"
                  className={`w-full rounded-md border ${errors.rgm ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting}
                />
                <datalist id="rgm_list">
                  {rgms.map(rgm => (
                    <option key={rgm} value={rgm} />
                  ))}
                </datalist>
                {errors.rgm && <p className="mt-1 text-sm text-red-500">{errors.rgm}</p>}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Type a new RGM or select from existing options
                </p>
              </div>

              {/* Area Coach Dropdown */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Area Coach*
                </label>
                <select
                  name="area_coach"
                  value={formData.area_coach}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.area_coach ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting || !!selectedStore}
                >
                  <option value="">Select an Area Coach</option>
                  {areaCoaches.map(coach => (
                    <option key={coach} value={coach}>
                      {coach}
                    </option>
                  ))}
                </select>
                {errors.area_coach && <p className="mt-1 text-sm text-red-500">{errors.area_coach}</p>}
                {selectedStore && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Area Coach locked from filter selection
                  </p>
                )}
              </div>

              {/* Regional Coach Dropdown */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Regional Coach*
                </label>
                <select
                  name="regional_coach"
                  value={formData.regional_coach}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.regional_coach ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting || !!selectedStore}
                >
                  <option value="">Select a Regional Coach</option>
                  {regionalCoaches.map(coach => (
                    <option key={coach} value={coach}>
                      {coach}
                    </option>
                  ))}
                </select>
                {errors.regional_coach && <p className="mt-1 text-sm text-red-500">{errors.regional_coach}</p>}
                {selectedStore && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Regional Coach locked from filter selection
                  </p>
                )}
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Serial Number
                </label>
                <input
                  type="text"
                  name="serial_number"
                  value={formData.serial_number}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 dark:bg-gray-700 dark:text-white"
                  disabled={isSubmitting}
                />
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quantity*
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  min="1"
                  className={`w-full rounded-md border ${errors.quantity ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting}
                />
                {errors.quantity && <p className="mt-1 text-sm text-red-500">{errors.quantity}</p>}
              </div>

                          </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="mr-3 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 focus:outline-none"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Record'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
}